// toolsService.js - Interactive Tools service
// DriftAlarm.AI - Built by Security Experts
// Provides API integration for security testing tools

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

// Tool definitions with metadata
export const TOOLS = [
    {
        id: 'curl',
        name: 'Curl',
        icon: '🌐',
        description: 'Execute custom HTTP requests',
        category: 'HTTP Testing',
        serverSide: true,
        fields: [
            { name: 'url', type: 'url', label: 'URL', required: true, placeholder: 'https://example.com/api' },
            { name: 'method', type: 'select', label: 'Method', options: ['GET', 'POST', 'HEAD'], default: 'GET' },
            { name: 'contentType', type: 'select', label: 'Content-Type', options: ['application/json', 'application/x-www-form-urlencoded', 'text/plain'], default: 'application/json' },
            { name: 'authorization', type: 'text', label: 'Authorization (optional)', placeholder: 'Bearer token...' },
            { name: 'body', type: 'textarea', label: 'Request Body (POST only)', placeholder: '{"key": "value"}', conditional: { field: 'method', value: 'POST' } }
        ]
    },
    {
        id: 'headers',
        name: 'Headers',
        icon: '📋',
        description: 'Analyze HTTP security headers',
        category: 'HTTP Testing',
        serverSide: true,
        fields: [
            { name: 'url', type: 'url', label: 'URL', required: true, placeholder: 'https://example.com' }
        ]
    },
    {
        id: 'ssl',
        name: 'SSL/TLS',
        icon: '🔒',
        description: 'Check SSL certificate and TLS config',
        category: 'Infrastructure',
        serverSide: true,
        fields: [
            { name: 'host', type: 'text', label: 'Host', required: true, placeholder: 'example.com' },
            { name: 'port', type: 'number', label: 'Port', default: 443, min: 1, max: 65535 }
        ]
    },
    {
        id: 'dns',
        name: 'DNS',
        icon: '📡',
        description: 'Query DNS records',
        category: 'Infrastructure',
        serverSide: true,
        fields: [
            { name: 'domain', type: 'text', label: 'Domain', required: true, placeholder: 'example.com' },
            { name: 'recordType', type: 'select', label: 'Record Type', options: ['ALL', 'A', 'AAAA', 'MX', 'TXT', 'NS', 'CNAME', 'SOA'], default: 'ALL' }
        ]
    },
    {
        id: 'whois',
        name: 'WHOIS',
        icon: '📝',
        description: 'Domain registration info',
        category: 'Infrastructure',
        serverSide: true,
        fields: [
            { name: 'domain', type: 'text', label: 'Domain', required: true, placeholder: 'example.com' }
        ]
    },
    {
        id: 'port',
        name: 'Port Check',
        icon: '🚪',
        description: 'Check if ports are open',
        category: 'Infrastructure',
        serverSide: true,
        fields: [
            { name: 'host', type: 'text', label: 'Host/IP', required: true, placeholder: 'example.com or 1.2.3.4' },
            { name: 'ports', type: 'text', label: 'Ports', required: true, placeholder: '80,443,8080 or 1-100' }
        ]
    },
    {
        id: 'subdomain',
        name: 'Subdomains',
        icon: '🔍',
        description: 'Quick subdomain enumeration',
        category: 'Reconnaissance',
        serverSide: true,
        fields: [
            { name: 'domain', type: 'text', label: 'Domain', required: true, placeholder: 'example.com' }
        ]
    },
    {
        id: 'cloud',
        name: 'Cloud Enum',
        icon: '☁️',
        description: 'Enumerate public cloud storage (S3, Azure Blob, GCP)',
        category: 'Reconnaissance',
        serverSide: true,
        timeout: 600000, // 10 minutes - cloud_enum can take 5-8 minutes
        fields: [
            { name: 'keywords', type: 'text', label: 'Keywords', required: true, placeholder: 'company, brand, example.com', hint: 'Enter a domain (e.g. example.com) to discover Azure tenants, or keywords to find open cloud storage buckets. This scan may take 5+ minutes.' },
            { name: 'aws', type: 'checkbox', label: 'AWS S3', default: true },
            { name: 'azure', type: 'checkbox', label: 'Azure Blob', default: true },
            { name: 'gcp', type: 'checkbox', label: 'GCP Storage', default: true }
        ]
    },
    {
        id: 'encoder',
        name: 'Encoder',
        icon: '🔄',
        description: 'URL/Base64 encode/decode',
        category: 'Utilities',
        serverSide: false,
        fields: [
            { name: 'input', type: 'textarea', label: 'Input', required: true, placeholder: 'Text to encode/decode' },
            { name: 'operation', type: 'select', label: 'Operation', options: ['URL Encode', 'URL Decode', 'Base64 Encode', 'Base64 Decode'], default: 'URL Encode' }
        ]
    }
];

/**
 * Helper to get auth token
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Get headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Execute a tool (server-side)
 * @param {string} toolId - Tool identifier
 * @param {object} params - Tool-specific parameters
 * @returns {Promise<object>} - { success, jobId, ... }
 */
export async function executeTool(toolId, params) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/tools-execute`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ toolId, params }),
        });

        const data = await response.json();

        if (!response.ok) {
            const error = new Error(data.error || data.message || 'Failed to execute tool');
            error.statusCode = response.status;
            error.upgradeRequired = data.upgradeRequired;
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error executing tool:', error);
        throw error;
    }
}

/**
 * Get tool execution result
 * @param {string} jobId - Job ID from executeTool
 * @returns {Promise<object>} - Result or status
 */
export async function getToolResult(jobId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/tools-result/${jobId}`, {
            method: 'GET',
            headers,
        });

        if (response.status === 202) {
            // Still processing
            return { ready: false, ...(await response.json()) };
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get result (${response.status})`);
        }

        return { ready: true, ...(await response.json()) };
    } catch (error) {
        console.error('Error getting tool result:', error);
        throw error;
    }
}

/**
 * Poll for tool completion
 * @param {string} jobId - Job ID
 * @param {function} onProgress - Progress callback (optional)
 * @param {number} maxWaitMs - Maximum wait time (default 2 minutes)
 * @returns {Promise<object>} - Final result
 */
export async function pollToolCompletion(jobId, onProgress = null, maxWaitMs = 120000) {
    const startTime = Date.now();
    const pollInterval = 2000; // 2 seconds

    while (Date.now() - startTime < maxWaitMs) {
        try {
            const result = await getToolResult(jobId);

            if (onProgress) {
                onProgress(result);
            }

            if (result.ready) {
                return result;
            }

            if (result.status === 'failed') {
                throw new Error(result.error || 'Tool execution failed');
            }

            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, pollInterval));

        } catch (error) {
            console.error('Poll error:', error);
            throw error;
        }
    }

    throw new Error('Tool execution timed out');
}

/**
 * Execute and wait for result (convenience method)
 * @param {string} toolId - Tool identifier
 * @param {object} params - Tool parameters
 * @param {function} onProgress - Progress callback (optional)
 * @param {number} maxWaitMs - Maximum wait time (optional, defaults based on tool)
 * @returns {Promise<object>} - Tool result
 */
export async function executeAndWait(toolId, params, onProgress = null, maxWaitMs = null) {
    const { jobId } = await executeTool(toolId, params);

    // Use tool-specific timeout if not provided
    if (!maxWaitMs) {
        const tool = TOOLS.find(t => t.id === toolId);
        maxWaitMs = tool?.timeout || 120000; // Default 2 minutes
    }

    return await pollToolCompletion(jobId, onProgress, maxWaitMs);
}

/**
 * Client-side encoding utility (instant, no server call)
 * @param {string} input - Input string
 * @param {string} operation - Operation type
 * @returns {object} - { success, result, error }
 */
export function encodeUtility(input, operation) {
    try {
        let result;
        switch (operation) {
            case 'URL Encode':
                result = encodeURIComponent(input);
                break;
            case 'URL Decode':
                result = decodeURIComponent(input);
                break;
            case 'Base64 Encode':
                result = btoa(unescape(encodeURIComponent(input)));
                break;
            case 'Base64 Decode':
                result = decodeURIComponent(escape(atob(input)));
                break;
            default:
                return { success: false, error: 'Unknown operation' };
        }
        return { success: true, result };
    } catch (error) {
        return { success: false, error: error.message };
    }
}

/**
 * Format command summary for display
 * @param {string} toolId - Tool ID
 * @param {object} params - Tool parameters
 * @returns {string} - Command summary string
 */
export function formatCommandSummary(toolId, params) {
    switch (toolId) {
        case 'curl':
            return `${params.method || 'GET'} ${params.url}`;
        case 'headers':
            return `headers ${params.url}`;
        case 'ssl':
            return `ssl ${params.host}:${params.port || 443}`;
        case 'dns':
            return `dns ${params.domain} ${params.recordType || 'ALL'}`;
        case 'whois':
            return `whois ${params.domain}`;
        case 'port':
            return `port ${params.host} ${params.ports}`;
        case 'subdomain':
            return `subdomain ${params.domain}`;
        case 'cloud':
            const providers = [params.aws && 'AWS', params.azure && 'Azure', params.gcp && 'GCP'].filter(Boolean).join(',');
            return `cloud ${params.keywords} [${providers}]`;
        case 'encoder':
            return `${params.operation}`;
        default:
            return `${toolId} ${JSON.stringify(params)}`;
    }
}

/**
 * Generate unique ID for history items
 * @returns {string} - Unique ID
 */
export function generateHistoryId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Named export for { toolsService } imports
export const toolsService = {
    TOOLS,
    executeTool,
    getToolResult,
    pollToolCompletion,
    executeAndWait,
    encodeUtility,
    formatCommandSummary,
    generateHistoryId
};

// Default export
export default toolsService;
